import { useState } from 'react'

import AddIcon from '@mui/icons-material/Add'
import AlarmOffRoundedIcon from '@mui/icons-material/AlarmOffRounded'
import AlarmRoundedIcon from '@mui/icons-material/AlarmRounded'
import CloseIcon from '@mui/icons-material/Close'
import SearchIcon from '@mui/icons-material/Search'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'

import LocalStorageButtons from '@components/LocalStorageButtons'
import { FilterAutosuggest } from '@components/toolbar/FilterAutosuggest'
import { FilterContactpersoon } from '@components/toolbar/FilterContactpersoon'
import FilterFromToDate from '@components/toolbar/FilterFromToDate'
import FilterFromToWeek from '@components/toolbar/FilterFromToWeek'
import FilterGridContainer from '@components/toolbar/FilterGridContainer'
import FilterGridItem from '@components/toolbar/FilterGridItem'
import { FilterMyList } from '@components/toolbar/FilterMyList'
import { FilterSelectObject } from '@components/toolbar/FilterSelectObject'
import { FilterSelectString } from '@components/toolbar/FilterSelectString'
import { FilterToggleButton } from '@components/toolbar/FilterToggleButton'
import type { FilterOption, GroupedFilterOption } from '@models/Filter'
import { filterVerantwoordelijkeOptionsByOrganisatie } from '@util/project-util'

import { useAuthenticatedUser } from '../../../auth/hooks/useAuthenticatedUser'
import { useLocalFilters } from '../../../core/hooks/useLocalFilters'
import type { GeblokkeerdeWeek } from '../../../planning/types/GeblokkeerdeWeek'
import type { ProcesstapHoofdleiding } from '../../../status/types/ProcesstapHoofdleiding'
import { StyledIconButton } from '../../../theme/components/StyledIconButton'
import { CreateEditProjectDialog } from '../../project/components/dialogs/CreateEditProjectDialog'
import { queryKey } from '../hooks/useProjectenSearch'
import type { MijlpaalColor } from '../types/Mijlpalen'
import type { ProjectenSearchFilters as Filters } from '../types/ProjectenSearch'

import { FilterSelectMijlpaal } from './FilterSelectMijlpaal'
import { MijlpaalColorPicker } from './MijlpaalColorPicker'

type Props = {
  filters: Filters
  initialFilters: Filters
  onChange: (filters: Filters, removeSort?: boolean) => void
  onSearchChange: (search: any) => void
  options: any
  onReset: () => void
  onRefresh: () => void
  filterOptions: {
    hoofdleiding: {
      netbeheerders: string[]
      aannemers: string[]
      gemeenten: string[]
      typeWerkTypes: string[]
      verantwoordelijken: GroupedFilterOption[]
      mijlpalen: { type: string; label: string }[]
      gebieden: FilterOption[]
    }
    labels: FilterOption[]
    processtappen: ProcesstapHoofdleiding[]
  }
  geblokkeerdeWeken: GeblokkeerdeWeek[]
}

const planningsverschilOptions: FilterOption[] = [
  { label: 'Wijziging', code: 'wijziging' },
  { label: 'Afwijking', code: 'afwijking' },
]

export const ProjectenFilters = ({
  filters,
  initialFilters,
  onChange,
  onSearchChange,
  options,
  onReset,
  onRefresh,
  filterOptions: {
    hoofdleiding: {
      netbeheerders,
      aannemers,
      gemeenten,
      typeWerkTypes,
      verantwoordelijken,
      mijlpalen,
      gebieden: hoofdleidingGebieden,
    },
    labels: labelOptions,
    processtappen,
  },
  geblokkeerdeWeken,
}: Props) => {
  const { canCreateProject, organisatie, canViewPlanningsverschil } =
    useAuthenticatedUser()
  const { localFilters, handleFilterChange, handleZoeken, handleReset } =
    useLocalFilters<Filters>({
      filters,
      initialFilters,
      onChange,
      onRefresh,
      onReset,
    })
  const [open, setOpen] = useState(false)

  const {
    active,
    netbeheerder,
    gebied,
    processtap,
    labels,
    search,
    fromWeek,
    toWeek,
    aannemer,
    typeWerk,
    gemeente,
    verantwoordelijke,
    contactpersoon,
    fromPlanningChanged,
    toPlanningChanged,
    planningsverschil,
    mijlpaal,
  } = localFilters

  const verantwoordelijkeOptions = filterVerantwoordelijkeOptionsByOrganisatie(
    verantwoordelijken,
    organisatie,
  )

  const [mijlpaalType = null, mijlpaalColor = null] = mijlpaal ?? []

  const handleMijlpaalColorChange = (newColor: MijlpaalColor | null) => {
    const type = mijlpaalType || null
    const newMijlpaal: Filters['mijlpaal'] = newColor
      ? [type ?? '', newColor]
      : type
        ? [type]
        : null

    handleFilterChange({ mijlpaal: newMijlpaal }, true)
  }

  return (
    <>
      <FilterGridContainer>
        <FilterGridItem>
          <FilterSelectObject
            options={hoofdleidingGebieden}
            label="Gebied"
            filterKey="gebied"
            value={gebied}
            onFilterChange={handleFilterChange}
            multiple
          />
        </FilterGridItem>

        <FilterGridItem>
          <FilterSelectString
            multiple
            value={processtap}
            options={processtappen}
            label="Processtap"
            filterKey="processtap"
            onFilterChange={handleFilterChange}
          />
        </FilterGridItem>

        <FilterGridItem minColumns="double">
          <FilterFromToWeek
            fromWeek={fromWeek}
            toWeek={toWeek}
            onFilterChange={handleFilterChange}
            label="Gepland"
          />
        </FilterGridItem>

        <FilterGridItem>
          <FilterSelectString
            value={netbeheerder}
            options={netbeheerders}
            label="Netbeheerder"
            filterKey="netbeheerder"
            onFilterChange={handleFilterChange}
          />
        </FilterGridItem>

        <FilterGridItem>
          <FilterSelectString
            value={aannemer}
            options={aannemers}
            label="Uitvoerende Aannemer"
            filterKey="aannemer"
            onFilterChange={handleFilterChange}
          />
        </FilterGridItem>

        <FilterGridItem>
          <FilterSelectString
            multiple
            value={typeWerk}
            options={typeWerkTypes}
            label="Type werk"
            filterKey="typeWerk"
            onFilterChange={handleFilterChange}
          />
        </FilterGridItem>

        <FilterGridItem>
          <FilterSelectObject
            value={verantwoordelijke}
            options={verantwoordelijkeOptions}
            label="Projectverantwoordelijke"
            filterKey="verantwoordelijke"
            onFilterChange={handleFilterChange}
          />
        </FilterGridItem>

        <FilterGridItem>
          <FilterContactpersoon
            value={contactpersoon}
            onFilterChange={handleFilterChange}
          />
        </FilterGridItem>

        <FilterGridItem>
          <FilterSelectString
            multiple
            value={gemeente}
            options={gemeenten}
            label="Gemeente"
            filterKey="gemeente"
            onFilterChange={handleFilterChange}
          />
        </FilterGridItem>

        {labelOptions.length > 0 && (
          <FilterGridItem>
            <FilterSelectObject
              multiple
              options={labelOptions}
              label="Labels"
              filterKey="labels"
              value={labels}
              onFilterChange={handleFilterChange}
            />
          </FilterGridItem>
        )}

        <FilterGridItem>
          <FilterSelectMijlpaal
            options={mijlpalen}
            value={mijlpaal}
            onFilterChange={handleFilterChange}
          />
        </FilterGridItem>

        <FilterGridItem minColumns="double">
          <FilterFromToDate
            fromDate={fromPlanningChanged}
            toDate={toPlanningChanged}
            onFilterChange={handleFilterChange}
            label="Planning gewijzigd"
            prop="PlanningChanged"
          />
        </FilterGridItem>

        {canViewPlanningsverschil && (
          <FilterGridItem>
            <FilterSelectObject
              multiple
              options={planningsverschilOptions}
              label="Planningsverschil"
              value={planningsverschil}
              filterKey="planningsverschil"
              onFilterChange={handleFilterChange}
            />
          </FilterGridItem>
        )}

        <FilterGridItem>
          <FilterAutosuggest
            search={search}
            options={options}
            onFilterChange={handleFilterChange}
            onSearchChange={onSearchChange}
          />
        </FilterGridItem>

        <FilterGridItem
          minColumns="full"
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <MijlpaalColorPicker
            color={mijlpaalColor}
            onChange={handleMijlpaalColorChange}
          />

          <StyledIconButton
            icon={<CloseIcon />}
            title="Reset filters"
            onClick={handleReset}
          />
          <StyledIconButton
            icon={<SearchIcon />}
            title="Filters toepassen"
            onClick={handleZoeken}
          />

          <FilterMyList
            myList={localFilters.myList}
            onFilterChange={handleFilterChange}
          />

          <FilterToggleButton
            filterKey="active"
            value={active}
            onFilterChange={handleFilterChange}
            activeIcon={<AlarmRoundedIcon />}
            inactiveIcon={<AlarmOffRoundedIcon />}
            activeTitle="Klik om ook inactieve projecten te tonen"
            inactiveTitle="Klik om alleen actieve projecten te tonen"
          />

          {canCreateProject && (
            <Tooltip title="Aanmaken hoofdleiding project">
              <IconButton onClick={() => setOpen(true)}>
                <AddIcon />
              </IconButton>
            </Tooltip>
          )}

          <LocalStorageButtons
            name={queryKey}
            storable={localFilters}
            onClickLoad={handleFilterChange}
          />
        </FilterGridItem>
      </FilterGridContainer>

      {canCreateProject && open && (
        <CreateEditProjectDialog
          handleClose={() => setOpen(false)}
          type="create"
          geblokkeerdeWeken={geblokkeerdeWeken}
        />
      )}
    </>
  )
}
